import { isForbidden } from '@cwos/api-client';

/**
 * What a panel should be showing, decided once instead of on 60 screens.
 *
 * A blank panel is three different sentences — "there is nothing", "you cannot
 * see it", "it did not load" — and the admin was rendering the FIRST one for
 * all three. The dominant shape was:
 *
 *     if (q.isLoading) return <SkeletonSet … />;
 *     const rows = q.data ?? [];                    // an error collapses to []
 *     rows.length === 0 ? <SuccessEmpty heading="Nothing waiting" /> : …
 *
 * `q.data ?? []` is where it goes wrong: a 500, a dropped connection and a
 * refused permission all arrive as `undefined`, become `[]`, and are then
 * reported to the reader as a fact about their organisation. "No open
 * exceptions", "Every started shift has a session", "Nobody's last position was
 * outside a work site" — all three of those were rendered from failed requests,
 * and the last two are safety claims.
 *
 * The audit found 24 panels doing it. Rather than add an `isError` branch 24
 * times and rely on the 25th author remembering, the decision is this function
 * and the states are rendered by `<ListState>` in `ListStates.tsx`.
 *
 * WHAT THIS CANNOT DO, stated rather than papered over: for most list reads
 * this API answers a scope miss with `200 []`, not 403 (documentation's
 * to-write, channels, notifications, service events, mileage — all narrow
 * silently inside the query, and `services/routes.ts` says so out loud: "an
 * out-of-scope event is MISSING rather than refused"). So `refused` only ever
 * fires where the server actually said 403, and no amount of client cleverness
 * can turn a scoped `200 []` into "you cannot see it". Where a viewer's scope
 * is knowable from their OWN permission set, `<ScopeNote>` states that next to
 * the list; where it is not, an empty list stays an empty list and the gap is
 * in the report rather than invented here.
 *
 * `GET /directory/assignments` used to be named in that list and no longer
 * belongs in it: without `directory:read_all` it now REFUSES — 403 for asking
 * without a `?workerId=`, and 403 again for naming a worker outside the
 * caller's caseload — rather than narrowing to nothing. So `refused` is the
 * live phase for that read, and a screen that treats its empty answer as
 * "nobody is assigned" is describing a state the route cannot produce.
 */
export type ListPhase =
  /** No answer yet, and nothing on screen to keep. */
  | { kind: 'loading' }
  /** The server said 403. An answer — never a retry. */
  | { kind: 'refused' }
  /**
   * Asked and could not be told.
   *
   * `error` rides along so the renderer can ask WHY before it promises a retry
   * will help. Optional, because a phase may be constructed by hand (tests, and
   * the two screens that fold several reads into one phase) and an absent error
   * correctly means "we do not know, treat it as retryable".
   *
   * It also catches the 403 this function's own `isForbidden` cannot see:
   * `isForbidden` is an `instanceof` test against `@cwos/api-client`'s class,
   * and `src/lib/api.ts` throws a plain `Error` carrying `.status`. Nothing
   * feeding `listPhase` uses that transport today, but the renderer reads the
   * status structurally, so if one ever does the refusal still lands as a
   * refusal instead of as a retry button.
   */
  | { kind: 'failed'; error?: unknown }
  /**
   * A refresh failed but the previous answer is still valid.
   *
   * `retainedEmpty` says whether that answer had rows in it. It exists because
   * "render the previous answer untouched" and "render `children`" are only the
   * same instruction when there were rows: `children` is the caller's row
   * mapping, and over zero rows it produces an empty `<ul>` or a table header
   * over an empty `<tbody>`. A panel whose last good answer was legitimately
   * none must keep SAYING none — "No open exceptions" was true when it was
   * fetched and a failed refresh does not unsay it.
   */
  | { kind: 'stale'; retainedEmpty: boolean }
  /** A real answer, and the answer is none. */
  | { kind: 'empty' }
  /** A real answer with rows in it. */
  | { kind: 'ready' };

/** The parts of a react-query result this decision needs. */
export interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: unknown;
}

/**
 * @param query the react-query result
 * @param rows  how many rows the payload yielded, or `undefined` for a panel
 *              that is not a list (a detail card has no empty state)
 */
export function listPhase(query: QueryLike, rows?: number): ListPhase {
  // First, and regardless of what is cached: a permission that has been taken
  // away is an answer about now, not a stale-data situation.
  if (query.isError && isForbidden(query.error)) return { kind: 'refused' };
  if (query.data === undefined) {
    if (query.isError) return { kind: 'failed', error: query.error };
    if (query.isLoading) return { kind: 'loading' };
    // Not loading, not errored, and no payload. react-query reports this for a
    // disabled query, which means the screen has not asked yet — the honest
    // rendering is the pending one, not an empty list.
    return { kind: 'loading' };
  }
  // A failed refresh over a payload that DID arrive keeps that payload — and
  // keeps whether it was empty. Testing `isError` before `rows` (and stopping
  // there) is what blanked nine panels: a cached `[]` is `data !== undefined`,
  // so it reached `stale`, and `stale` rendered the caller's row mapping over
  // zero rows — a heading with nothing under it, and no error text or retry to
  // explain the void. The empty state is not something a failed refresh
  // removes; it is what the last good answer said.
  if (query.isError) return { kind: 'stale', retainedEmpty: rows === 0 };
  return rows === 0 ? { kind: 'empty' } : { kind: 'ready' };
}

/**
 * Why a worker's name is missing — the same three-way distinction, one row at a
 * time.
 *
 * Four screens built a `Map` from the directory and fell back to the literal
 * string `'A worker you cannot view'` for anything not in it. That sentence is
 * a claim about PERMISSION, and it was being printed for three different
 * reasons: a directory read that failed, a directory read that had not returned
 * yet, and an id genuinely outside the reader's scope. On `Timesheets` and
 * `Scheduling` the directory query is a secondary read with no error branch at
 * all, so a single 500 relabelled every worker on the screen as someone the
 * reader was not allowed to see.
 *
 * Only the third case is a permission statement. The other two say so.
 */
export interface NameSource {
  data: ReadonlyArray<{ id: string; displayName: string }> | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function nameResolver(
  source: NameSource,
  /** False when the reader does not hold `directory:read`, so the query never ran. */
  canReadDirectory = true,
): (id: string) => string {
  const map = new Map<string, string>();
  for (const w of source.data ?? []) map.set(w.id, w.displayName);
  return (id: string) => {
    const known = map.get(id);
    if (known !== undefined) return known;
    // Ordered by what the client actually knows, most certain first.
    if (!canReadDirectory) return 'A worker you cannot view';
    if (source.isError) return 'Name unavailable';
    if (source.data === undefined) return 'Loading…';
    // The directory answered and this id was not in it: scope is the honest
    // reading, and it is the only branch entitled to say so.
    return 'A worker you cannot view';
  };
}
